import { ROUTES } from '../../routes';
import { mapLotsToBatches } from '../sync-mappers';
import { normalizeTemplate } from '../template-storage';

const LEGACY_TEMPLATES_STORAGE_KEY = 'sp_templates';

function readLegacyTemplatesFromBrowser() {
  try {
    const raw = localStorage.getItem(LEGACY_TEMPLATES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizedTemplateToApiBody(tpl, companyId) {
  return {
    company_id: companyId,
    name: tpl.n,
    description: tpl.description || null,
    payment_type: tpl.paymentType || 'mass',
    file_type: tpl.fileType || null,
    delimiter: tpl.sep || null,
    reconciliation: tpl.strat || 'BY_CPF',
    identifier_field_mode: tpl.identifierFieldMode || null,
    identifier_field_location: tpl.identifierFieldLocation || null,
    allowed_users: Array.isArray(tpl.allowedUsers) ? tpl.allowedUsers : [],
    active: tpl.ativo !== false,
  };
}

const SCOPE_KEYS = ['me', 'companies', 'lots', 'approvals', 'users', 'templates'];

function normalizeScopes(scopeInput) {
  if (scopeInput == null || scopeInput === '' || scopeInput === 'full') {
    return [...SCOPE_KEYS];
  }
  if (scopeInput === 'companies') return ['companies'];
  if (scopeInput === 'users') return ['users'];
  if (scopeInput === 'templates') return ['templates'];
  if (scopeInput === 'batches') return ['lots', 'approvals'];
  if (Array.isArray(scopeInput)) {
    const set = new Set(scopeInput.filter((s) => SCOPE_KEYS.includes(s)));
    if (set.has('lots') || set.has('approvals')) {
      set.add('lots');
      set.add('approvals');
    }
    return set.size ? [...set] : [...SCOPE_KEYS];
  }
  return [...SCOPE_KEYS];
}

function createSyncActions(deps) {
  const {
    apiFetch,
    state,
    setAuthToken,
    setRoute,
    setParams,
    setCurrentUser,
    setCompanies,
    setTemplates,
    setUsers,
    setApprovals,
    setBatches,
    setIsSyncing,
    setWorkspaceReady,
    setActiveCompanyId,
    toast,
  } = deps;

  const syncFromApi = async (tokenArg = state.authToken, scopeInput) => {
    if (!tokenArg) return;
    const scopes = normalizeScopes(scopeInput);
    setIsSyncing(true);
    try {
      const tasks = [];
      if (scopes.includes('me')) {
        tasks.push(['me', () => apiFetch('/api/auth/me', {}, tokenArg)]);
      }
      if (scopes.includes('companies')) {
        tasks.push(['companies', () => apiFetch('/api/companies', {}, tokenArg)]);
      }
      if (scopes.includes('lots')) {
        tasks.push(['lots', () => apiFetch('/api/lots', {}, tokenArg)]);
      }
      if (scopes.includes('approvals')) {
        tasks.push(['approvals', () => apiFetch('/api/approvals', {}, tokenArg)]);
      }
      if (scopes.includes('users')) {
        tasks.push(['users', () => apiFetch('/api/users', {}, tokenArg)]);
      }
      if (scopes.includes('templates')) {
        const companyId = Number(state.activeCompanyId || state.currentUser?.company_id || 0);
        const query = companyId ? `?company_id=${companyId}` : '';
        tasks.push(['templates', () => apiFetch(`/api/templates${query}`, {}, tokenArg)]);
      }

      const results = await Promise.all(tasks.map(([, fn]) => fn()));
      const byKey = Object.fromEntries(tasks.map(([k], i) => [k, results[i]]));

      if (byKey.me) {
        const me = byKey.me;
        setCurrentUser({
          id: me?.id || null,
          name: me?.name || 'Usuário',
          role: me?.role || 'Perfil',
          email: me?.email || '',
          permissions: Array.isArray(me?.permissions) ? me.permissions : [],
          avatar_data: me?.avatar_data || '',
          company_id: me?.company_id != null ? Number(me.company_id) : null,
          debug_enabled: me?.debug_enabled === 'S' ? 'S' : 'N',
        });
      }

      if (byKey.companies !== undefined) {
        const companiesData = byKey.companies?.data || [];
        setCompanies(companiesData);
        if (companiesData.length) {
          const currentCompanyId = Number(state.activeCompanyId || 0);
          const currentCompany = companiesData.find((c) => Number(c.id) === currentCompanyId) || null;
          const currentIsActive = String(currentCompany?.status || '').toLowerCase() === 'active';
          const activeCompanies = companiesData.filter((c) => String(c.status || '').toLowerCase() === 'active');
          if (!currentCompany || !currentIsActive) {
            if (activeCompanies.length === 1) {
              setActiveCompanyId(activeCompanies[0].id);
            } else {
              setActiveCompanyId(0);
            }
          }
        }
      }

      if (byKey.lots !== undefined || byKey.approvals !== undefined) {
        const lotsData = byKey.lots?.data || [];
        const approvalsData = byKey.approvals?.data || [];
        const mappedBatches = mapLotsToBatches(lotsData, approvalsData);
        setApprovals(approvalsData);
        setBatches(mappedBatches);
      }

      if (byKey.users !== undefined) {
        setUsers(byKey.users?.data || []);
      }

      if (byKey.templates !== undefined) {
        let templatesData = byKey.templates?.data || [];
        let normalized = templatesData.map(normalizeTemplate).filter(Boolean);

        if (normalized.length === 0) {
          const legacyList = readLegacyTemplatesFromBrowser();
          const companyIdFromMe = byKey.me?.company_id != null ? Number(byKey.me.company_id) : 0;
          const firstCompanyId = Array.isArray(byKey.companies?.data) && byKey.companies.data[0]?.id != null
            ? Number(byKey.companies.data[0].id)
            : 0;
          const companyId = Number(
            companyIdFromMe
            || firstCompanyId
            || state.activeCompanyId
            || state.currentUser?.company_id
            || 0,
          );
          if (legacyList?.length && companyId > 0) {
            let posted = 0;
            for (const raw of legacyList) {
              const tpl = normalizeTemplate(raw);
              if (!tpl?.n) continue;
              try {
                await apiFetch('/api/templates', {
                  method: 'POST',
                  body: JSON.stringify(normalizedTemplateToApiBody(tpl, companyId)),
                }, tokenArg);
                posted += 1;
              } catch {
                /* um item inválido não bloqueia os demais */
              }
            }
            if (posted > 0) {
              const query = `?company_id=${companyId}`;
              const fresh = await apiFetch(`/api/templates${query}`, {}, tokenArg).catch(() => null);
              templatesData = fresh?.data || [];
              normalized = templatesData.map(normalizeTemplate).filter(Boolean);
              try {
                localStorage.removeItem(LEGACY_TEMPLATES_STORAGE_KEY);
              } catch {
                /* ignore */
              }
              toast('info', 'Modelos recuperados', 'Layouts que estavam só no navegador foram enviados ao servidor.');
            }
          }
        }

        setTemplates(normalized);
      }

    } catch (error) {
      if (error.status === 401) {
        setAuthToken('');
        setRoute(ROUTES.LOGIN);
        setParams({});
        setCompanies([]);
        setUsers([]);
        setApprovals([]);
        setBatches([]);
        toast('info', 'Sessão expirada', 'Faça login novamente.');
        return;
      }
      toast('error', 'Falha ao sincronizar', error.message);
    } finally {
      setIsSyncing(false);
      setWorkspaceReady(true);
    }
  };

  return { syncFromApi };
}

export { createSyncActions };
